import type { ShopVariant } from '../../api/types';

/**
 * The pure, DB-free half of R-04's order entry: parsing a pasted block and
 * validating a grid row.
 *
 * In its own module because `Order.tsx` exports components, and a file that
 * exports both components and plain functions breaks React Fast Refresh —
 * which is also why these were the only four eslint warnings on that file.
 * The split is what the rule is actually asking for: the logic under test
 * lives apart from the screen that renders it, and `Order.spec.ts` imports it
 * here without pulling a React tree behind it.
 */

// -------------------------------------------------------------------------- paste door

/**
 * CT-10 — ported near-verbatim from `retailer-ordering.html:369-372`'s
 * `applyPaste()`, validated against the live catalog instead of a static
 * array. Exported for its own vitest coverage (`Order.spec.ts`) — pure and
 * DB-free, the cheapest suite that can prove it.
 */
export function parsePaste(
  text: string,
  catalog: readonly ShopVariant[],
): { entries: Array<{ code: string; qty: number }>; missed: string[] } {
  const entries: Array<{ code: string; qty: number }> = [];
  const missed: string[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^([A-Za-z0-9-]+)[\s,;]+(\d+)$/);
    const rawCode = m?.[1];
    const rawQty = m?.[2];
    if (!rawCode || !rawQty) { missed.push(line); continue; }
    const variant = catalog.find((v) => v.code.toLowerCase() === rawCode.toLowerCase());
    if (!variant) { missed.push(rawCode); continue; }
    // Any quantity from 1 upward — there is no minimum to enforce (D4, CR-07).
    entries.push({ code: variant.code, qty: Math.max(1, Math.round(parseInt(rawQty, 10))) });
  }
  return { entries, missed };
}

// -------------------------------------------------------------------------- grid door
//
// R-04's default surface, matching the grid `retailer-portal.html`'s `VIEWS.bulk`
// draws (prototype line refs in this section's comments point there) — type or
// paste a code, quantity and (where the product needs one) a size, validated
// row by row, entirely client-side until "Add to cart" is pressed.

/** One editable grid row. A row with an empty code is simply unused — never an
 *  error, and never counted toward the valid-line summary. */
export interface GridRow {
  id: string;
  code: string;
  qty: string;
  size: string;
  /** Resolved via `GET /shop/catalog/:code` on the code cell losing focus.
   *  `null` until resolved, or when the code is empty/unrecognised. */
  variant: ShopVariant | null;
  sizeOptions: string[];
  resolving: boolean;
  notFound: boolean;
}

export function emptyGridRow(id: string): GridRow {
  return { id, code: '', qty: '', size: '', variant: null, sizeOptions: [], resolving: false, notFound: false };
}

export type GridRowError = 'unrecognised' | 'quantity' | 'size' | null;

/**
 * Pure and DB-free, same testing rationale as `parsePaste` above. Mirrors
 * `retailer-portal.html:1832-1839`'s three cases exactly: an empty code is
 * never an error; an unrecognised one is; a recognised one still needs a
 * valid quantity, and a size/length when (and only when) `sizeOptions` is
 * non-empty for that product (CR-44).
 */
export function gridRowError(row: GridRow): GridRowError {
  if (!row.code.trim() || row.resolving) return null;
  if (row.notFound || !row.variant) return 'unrecognised';
  const qty = parseInt(row.qty, 10);
  if (!row.qty.trim() || !Number.isFinite(qty) || qty < 1) return 'quantity';
  if (row.sizeOptions.length > 0 && !row.size) return 'size';
  return null;
}

/**
 * Not just "no error" — a row mid-lookup has no error yet either (so the UI
 * doesn't flash "unrecognised" while the request is in flight), but it is
 * not valid: `variant` is still `null` there, and treating it as valid would
 * hand a null variant to the price/total calculation the moment "Add to
 * cart" or the running total reads it.
 */
export function gridRowIsValid(row: GridRow): boolean {
  return row.code.trim() !== '' && !row.resolving && row.variant !== null && gridRowError(row) === null;
}
